import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { adminService } from '../../services/adminService';
import RoleBasedNavigation from '../../components/ui/RoleBasedNavigation';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import PlatformMetricsCard from './components/PlatformMetricsCard';
import TierDistributionChart from './components/TierDistributionChart';
import RevenueChart from '../supplier-dashboard/components/RevenueChart';
import TierThresholdConfig from './components/TierThresholdConfig';
import SupplierTierTable from './components/SupplierTierTable';
import SystemSettingsPanel from './components/SystemSettingsPanel';
import UpcomingUpgradesCard from './components/UpcomingUpgradesCard';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedTab, setSelectedTab] = useState('overview');

  const [platformAnalytics, setPlatformAnalytics] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [tierThresholds, setTierThresholds] = useState(null);
  const [revenueTrends, setRevenueTrends] = useState([]);
  const [upcomingUpgrades, setUpcomingUpgrades] = useState([]);
  const [countries, setCountries] = useState([]);
  const [currencies, setCurrencies] = useState([]);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    loadDashboardData();
  }, [user]);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      // Load all admin data in parallel
      const [analyticsRes, suppliersRes, thresholdsRes, trendsRes, upgradesRes, countriesRes, currenciesRes] = await Promise.all([
        adminService?.getPlatformAnalytics(),
        adminService?.getAllSuppliers(),
        adminService?.getTierThresholds(),
        adminService?.getRevenueTrends(),
        adminService?.getSuppliersNearTierUpgrade(),
        adminService?.getSupportedCountries(),
        adminService?.getSupportedCurrencies()
      ]);

      if (analyticsRes?.error) throw analyticsRes?.error;
      if (suppliersRes?.error) throw suppliersRes?.error;

      setPlatformAnalytics(analyticsRes?.data);
      setSuppliers(suppliersRes?.data || []);
      setTierThresholds(thresholdsRes?.data);
      setRevenueTrends(trendsRes?.data || []);
      setUpcomingUpgrades(upgradesRes?.data || []);
      setCountries(countriesRes?.data || []);
      setCurrencies(currenciesRes?.data || []);
    } catch (err) {
      setError(err?.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateThresholds = async (newThresholds) => {
    try {
      const { error } = await adminService?.updateTierThresholds(newThresholds);
      if (error) throw error;
      setTierThresholds(newThresholds);
      // Reload analytics to reflect changes
      const { data } = await adminService?.getPlatformAnalytics();
      setPlatformAnalytics(data);
    } catch (err) {
      console.error('Failed to update thresholds:', err);
    }
  };

  const handleUpdateCountries = async (newCountries) => {
    try {
      const { error } = await adminService?.updateSupportedCountries(newCountries);
      if (error) throw error;
      setCountries(newCountries);
    } catch (err) {
      console.error('Failed to update countries:', err);
    }
  };

  const handleUpdateCurrencies = async (newCurrencies) => {
    try {
      const { error } = await adminService?.updateSupportedCurrencies(newCurrencies);
      if (error) throw error;
      setCurrencies(newCurrencies);
    } catch (err) {
      console.error('Failed to update currencies:', err);
    }
  };

  const handleSupplierClick = (supplier) => {
    // Navigate to supplier profile or show details modal
    console.log('View supplier:', supplier);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="Loader2" size={48} className="animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="AlertCircle" size={48} className="text-error mx-auto mb-4" />
          <p className="text-error mb-4">{error}</p>
          <Button onClick={loadDashboardData}>Retry</Button>
        </div>
      </div>
    );
  }

  const metrics = [
    {
      title: 'Total Suppliers',
      value: platformAnalytics?.totalSuppliers?.toString() || '0',
      subtitle: `${suppliers?.filter(s => s?.is_verified)?.length || 0} verified`,
      icon: 'Users',
      iconColor: 'var(--color-primary)',
      trend: '+12.5%',
      trendType: 'positive'
    },
    {
      title: 'Platform Revenue',
      value: `$${platformAnalytics?.totalRevenue?.toLocaleString() || '0'}`,
      subtitle: 'Total lifetime revenue',
      icon: 'DollarSign',
      iconColor: 'var(--color-success)',
      trend: '+23.8%',
      trendType: 'positive'
    },
    {
      title: 'Total Orders',
      value: platformAnalytics?.totalOrders?.toString() || '0',
      subtitle: 'All-time orders',
      icon: 'ShoppingBag',
      iconColor: 'var(--color-accent)',
      trend: '+18.2%',
      trendType: 'positive'
    },
    {
      title: 'Average Rating',
      value: platformAnalytics?.averageRating?.toFixed(1) || '0.0',
      subtitle: 'Platform-wide',
      icon: 'Star',
      iconColor: 'var(--color-warning)',
      trend: '+0.2',
      trendType: 'positive'
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedNavigation />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
              <Icon name="Shield" size={28} color="var(--color-primary)" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground">Admin Dashboard</h1>
              <p className="text-muted-foreground">Platform management and analytics</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {['overview', 'suppliers', 'settings']?.map((tab) => (
            <button
              key={tab}
              onClick={() => setSelectedTab(tab)}
              className={`px-6 py-3 rounded-lg font-medium transition-smooth capitalize whitespace-nowrap ${
                selectedTab === tab
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-card text-muted-foreground hover:bg-muted'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {selectedTab === 'overview' && (
          <div className="space-y-6">
            {/* Platform Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {metrics?.map((metric, index) => (
                <PlatformMetricsCard key={index} {...metric} />
              ))}
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <TierDistributionChart tierData={platformAnalytics?.tierDistribution} />
              <div className="bg-card rounded-xl shadow-warm p-4 md:p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Icon name="TrendingUp" size={24} color="var(--color-primary)" />
                  <h3 className="text-lg md:text-xl font-heading font-bold text-foreground">Revenue Trends</h3>
                </div>
                <RevenueChart data={revenueTrends} chartType="line" />
              </div>
            </div>

            {/* Tier Management & Upcoming Upgrades */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <TierThresholdConfig 
                thresholds={tierThresholds} 
                onUpdate={handleUpdateThresholds}
                loading={loading}
              />
              <UpcomingUpgradesCard suppliers={upcomingUpgrades} />
            </div>
          </div>
        )}

        {/* Suppliers Tab */}
        {selectedTab === 'suppliers' && (
          <div className="space-y-6">
            <SupplierTierTable 
              suppliers={suppliers} 
              onSupplierClick={handleSupplierClick}
            />
          </div>
        )}

        {/* Settings Tab */}
        {selectedTab === 'settings' && (
          <div className="space-y-6">
            <SystemSettingsPanel
              countries={countries}
              currencies={currencies}
              onUpdateCountries={handleUpdateCountries}
              onUpdateCurrencies={handleUpdateCurrencies}
              loading={loading}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;